/**
 * 订阅服务模块 — 对接 GET /user/api/subscription/status
 *
 * 数据流：fetchSubscriptionStatus → applySubscriptionStatus → subscriptionState
 * 页面推荐直接读 subscriptionState.view（由 buildView 一次推导）
 */
import Vue from "vue";
import request from "@/utils/request";

/** 订阅接口根路径 */
export const SUBSCRIPTION_API_BASE = "/api/user/api/subscription";

/** 协议签署接口根路径 */
export const AGREEMENT_API_BASE = "/api/user/api/agreement";

/** 协议类型：0 试用协议 TRIAL-V1.0 */
export const AGREEMENT_TYPE_TRIAL = 0;

/** 协议类型：1 正式协议 FORMAL-V1.0 */
export const AGREEMENT_TYPE_FORMAL = 1;

/**
 * 后端 serviceStatus 枚举
 * TRIAL=0 试用阶段 · FORMAL=1 正式有效 · EXPIRED=2 已过期
 */
export const SERVICE_STATUS = { TRIAL: 0, FORMAL: 1, EXPIRED: 2 };

/**
 * 后端 trialStatus 枚举（仅 serviceStatus=0 时有意义）
 * NOT_APPLIED=0 未申请 · ACTIVE=1 试用中 · EXPIRED=2 试用已到期
 */
export const TRIAL_STATUS = { NOT_APPLIED: 0, ACTIVE: 1, EXPIRED: 2 };

/**
 * 个人中心侧栏 / 页面布局的 UI 模式（buildView 的 mode 字段取值）
 * apply 未开通 · trial 试用倒计时 · trial-expired 试用到期
 * formal 正式/预购 VIP · expired 正式服务到期
 */
export const VIEW_MODE = {
  APPLY: "apply",
  TRIAL: "trial",
  TRIAL_EXPIRED: "trial-expired",
  FORMAL: "formal",
  EXPIRED: "expired",
};

/** @deprecated 请用 VIEW_MODE */
export const SUBSCRIPTION_VIEW_MODE = VIEW_MODE;

/**
 * 返回默认 UI 视图对象（未加载 / 无数据 / 重置时用）
 *
 * 各字段初始均为 false，mode 为 apply
 *
 * @returns {{
 *   mode: string,
 *   readonly: boolean,
 *   claim: boolean,
 *   trialBanner: boolean,
 *   countdown: boolean,
 *   vip: boolean,
 *   active: boolean,
 *   list: boolean,
 *   grayLayout: boolean,
 *   purchaseTip: boolean,
 *   prepaid: boolean
 * }}
 */
export function emptyView() {
  return {
    mode: VIEW_MODE.APPLY,
    readonly: false,
    claim: false,
    trialBanner: false,
    countdown: false,
    vip: false,
    active: false,
    list: false,
    grayLayout: false,
    purchaseTip: false,
    prepaid: false,
  };
}

/** @deprecated 请用 emptyView */
export const createEmptySubscriptionView = emptyView;

/**
 * 订阅全局状态（Vue.observable，indiv 各页面共享）
 *
 * @property {boolean} loading     是否正在请求 /status
 * @property {boolean} loaded      是否至少成功拉取过一次
 * @property {number}  trialDays     可领取试用天数（默认 14）
 * @property {object|null} status    GET /status 原始响应
 * @property {object|null} activeData  到期日快照（buildActive 产出，兼容原 /active）
 * @property {ReturnType<emptyView>} view  UI 视图模型（页面主读此对象）
 */
export const subscriptionState = Vue.observable({
  loading: false,
  loaded: false,
  trialDays: 14,
  status: null,
  activeData: null,
  view: emptyView(),
});

/** 进行中的 /status 请求 Promise，用于并发去重 */
let inflightStatusRequest = null;

// ── 内部小工具 ──────────────────────────────────────────

/**
 * 将接口字段安全转为 number；空串 / null / 非数字 → null
 * @param {*} value
 * @returns {number|null}
 */
function num(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * 解析 "HH:mm:ss" 时分秒；无时间部分则默认当天 23:59:59.999（纯日期按整天结束）
 * @param {string|undefined} timePart
 * @returns {{ hour: number, minute: number, second: number, ms: number }}
 */
function parseTimeOfDay(timePart) {
  const endOfDay = { hour: 23, minute: 59, second: 59, ms: 999 };
  if (!timePart) return endOfDay;
  const parts = timePart.split(":").map(Number);
  if (parts.length < 3 || parts.some(isNaN)) return endOfDay;
  const [hour, minute, second] = parts;
  return { hour, minute, second, ms: 0 };
}

/**
 * 数字补零为两位字符串（日期格式化用）
 * @param {number} value
 * @returns {string}
 */
function padTwo(value) {
  return String(value).padStart(2, "0");
}

/**
 * 从日期时间字符串截取日期部分（YYYY-MM-DD）
 * @param {string|null|undefined} value
 * @returns {string}
 */
function toDateOnly(value) {
  if (!value) return "";
  return String(value).trim().split(/\s+/)[0];
}

/**
 * 根据到期时间戳计算剩余整天数（向上取整，最小 0）
 * @param {number} endTimestamp 毫秒时间戳
 * @returns {number|null}
 */
function computeDaysLeft(endTimestamp) {
  if (!endTimestamp) return null;
  return Math.max(0, Math.ceil((endTimestamp - Date.now()) / 86400000));
}

/**
 * 是否已有服务记录（hasService=true，区别于从未开通）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function hasSvc(status) {
  return Boolean(status && typeof status === "object" && status.hasService);
}

/**
 * 试用期内是否已预购正式服务（pendingYears > 0）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function prepaid(status) {
  return Boolean(status && Number(status.pendingYears) > 0);
}

/**
 * 是否曾有正式服务记录（有 formalStartTime 或 formalEndTime）
 * 用于区分「试用到期横幅」与「正式服务到期」
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function hadFormal(status) {
  if (!status || typeof status !== "object") return false;
  return Boolean(status.formalEndTime || status.formalStartTime);
}

/**
 * 试用是否已到期
 *
 * 仅在 serviceStatus=0（试用阶段）时判断；serviceStatus=1 正式有效时恒为 false。
 * 优先看 trialStatus=2；若后端仍为 1，则按 trialEndTime 客户端兜底。
 *
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function trialEnded(status) {
  if (!hasSvc(status)) return false;
  const ss = num(status.serviceStatus);
  const ts = num(status.trialStatus);
  if (ss === SERVICE_STATUS.FORMAL) return false;
  if (ss !== SERVICE_STATUS.TRIAL) return false;
  if (ts === TRIAL_STATUS.EXPIRED) return true;
  if (ts === TRIAL_STATUS.ACTIVE && status.trialEndTime) {
    return parseTrialEndTimestamp(status.trialEndTime) <= Date.now();
  }
  return false;
}

/**
 * 订阅是否处于只读态（评测页列表仅可删除，不可添加/编辑）
 *
 * 满足其一即为只读：
 * 1. serviceStatus=2 正式服务整体过期
 * 2. serviceStatus=0 且试用已到期且未预购（pendingYears=0）
 *
 * 例外：试用中已预购（pendingYears>0）时试用到期仍全功能
 *
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function isReadonly(status) {
  if (!hasSvc(status)) return false;
  const ss = num(status.serviceStatus);
  if (ss === SERVICE_STATUS.EXPIRED) return true;
  if (ss === SERVICE_STATUS.FORMAL) return false;
  if (ss !== SERVICE_STATUS.TRIAL) return false;
  if (prepaid(status)) return false;
  return trialEnded(status);
}

/**
 * 服务是否全功能可用（添加、编辑、排序等）
 * 条件：有服务记录 + serviceStatus 为 0 或 1 + 非只读
 *
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function isActive(status) {
  if (isReadonly(status)) return false;
  if (!hasSvc(status)) return false;
  const ss = num(status.serviceStatus);
  return ss === SERVICE_STATUS.TRIAL || ss === SERVICE_STATUS.FORMAL;
}

/**
 * 是否显示顶部试用倒计时横幅（claimTrial 组件）
 *
 * 显示：有服务、未预购、试用中或试用已到期（含倒计时 00）
 * 不显示：未开通、正式有效、已预购、正式买过且已过期（serviceStatus=2 且有正式记录）
 *
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
function showTrialBanner(status) {
  if (!hasSvc(status)) return false;
  if (prepaid(status)) return false;
  const ss = num(status.serviceStatus);
  const ts = num(status.trialStatus);
  if (ss === SERVICE_STATUS.FORMAL) return false;
  if (ss === SERVICE_STATUS.EXPIRED && hadFormal(status)) return false;
  if (ss !== SERVICE_STATUS.TRIAL && ss !== SERVICE_STATUS.EXPIRED) return false;
  return (
    ts === TRIAL_STATUS.ACTIVE ||
    ts === TRIAL_STATUS.EXPIRED ||
    trialEnded(status)
  );
}

/**
 * 推导个人中心侧栏 / 布局主模式（buildView.mode）
 *
 * 按优先级依次判断，命中即返回：
 * ① hasService=false        → apply（领取试用卡片）
 * ② serviceStatus=1         → formal（VIP 生效）
 * ③ serviceStatus=0 已预购  → formal（试用预购按 VIP 展示）
 * ④ serviceStatus=0 试用到期未预购 → trial-expired
 * ⑤ showTrialBanner         → trial（侧栏倒计时）
 * ⑥ serviceStatus=2 或只读  → expired（正式到期）
 *
 * @param {object|null|undefined} status
 * @returns {string} VIEW_MODE.*
 */
function resolveMode(status) {
  if (!hasSvc(status)) return VIEW_MODE.APPLY;

  const ss = num(status.serviceStatus);
  const prep = prepaid(status);

  if (ss === SERVICE_STATUS.FORMAL) return VIEW_MODE.FORMAL;
  if (ss === SERVICE_STATUS.TRIAL && prep) return VIEW_MODE.FORMAL;
  if (ss === SERVICE_STATUS.TRIAL && !prep && trialEnded(status)) {
    return VIEW_MODE.TRIAL_EXPIRED;
  }
  if (showTrialBanner(status)) return VIEW_MODE.TRIAL;
  if (ss === SERVICE_STATUS.EXPIRED || isReadonly(status)) {
    return VIEW_MODE.EXPIRED;
  }
  return VIEW_MODE.APPLY;
}

// ── 时间 / 到期展示 ─────────────────────────────────────

/**
 * 解析试用结束时间为毫秒时间戳
 * 纯日期（无时分秒）按当天 23:59:59.999 计算
 *
 * @param {string|number|null|undefined} trialEndTime
 * @returns {number} 无效时返回 0
 */
export function parseTrialEndTimestamp(trialEndTime) {
  if (!trialEndTime) return 0;
  const raw = String(trialEndTime).trim();
  const [datePart, timePart] = raw.split(/\s+/);
  const dateParts = datePart.split("-").map(Number);
  if (dateParts.length !== 3 || dateParts.some(isNaN)) return 0;
  const [year, month, day] = dateParts;
  const { hour, minute, second, ms } = parseTimeOfDay(timePart);
  const ts = new Date(year, month - 1, day, hour, minute, second, ms).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * 在日期时间字符串上叠加年数，保留原字符串是否带时分秒的格式
 *
 * @param {string|null|undefined} dateTimeStr 如 "2026-07-03" 或 "2026-07-03 12:00:00"
 * @param {number} years 叠加年数
 * @returns {string}
 */
export function addYearsToDateTime(dateTimeStr, years) {
  if (!dateTimeStr) return "";
  const addYears = Number(years);
  if (!Number.isFinite(addYears) || addYears <= 0) {
    return String(dateTimeStr).trim();
  }
  const raw = String(dateTimeStr).trim();
  const [datePart, timePart] = raw.split(/\s+/);
  const dateParts = datePart.split("-").map(Number);
  if (dateParts.length !== 3 || dateParts.some(isNaN)) return raw;
  const [year, month, day] = dateParts;
  const { hour, minute, second, ms } = parseTimeOfDay(timePart);
  const next = new Date(year, month - 1, day, hour, minute, second, ms);
  next.setFullYear(next.getFullYear() + addYears);
  const nextDate = `${next.getFullYear()}-${padTwo(next.getMonth() + 1)}-${padTwo(
    next.getDate()
  )}`;
  if (timePart) {
    return `${nextDate} ${padTwo(next.getHours())}:${padTwo(
      next.getMinutes()
    )}:${padTwo(next.getSeconds())}`;
  }
  return nextDate;
}

/**
 * 试用期内预购时，将预购年数叠加到 trialEndTime 后的展示用结束时间
 * 未预购则直接返回 trialEndTime
 *
 * @param {object|null|undefined} status
 * @returns {string}
 */
export function getEffectiveTrialEndTime(status) {
  if (!status || typeof status !== "object" || !status.trialEndTime) return "";
  const years = Number(status.pendingYears) || 0;
  if (years <= 0) return status.trialEndTime;
  return addYearsToDateTime(status.trialEndTime, years);
}

/**
 * 订阅展示用到期时间（侧栏到期日、activeData.expireTime 等）
 *
 * - 正式服务 serviceStatus=1：formalEndTime
 * - 试用预购且试用未到期：trialEndTime + pendingYears
 * - 试用预购且试用已到期：formalEndTime
 * - 其他试用：trialEndTime
 *
 * @param {object|null|undefined} status
 * @returns {string}
 */
export function getSubscriptionExpireTime(status) {
  if (!status || typeof status !== "object") return "";
  const ss = num(status.serviceStatus);
  if (ss === SERVICE_STATUS.FORMAL) return status.formalEndTime || "";
  const years = Number(status.pendingYears) || 0;
  if (years > 0) {
    if (trialEnded(status) && status.formalEndTime) return status.formalEndTime;
    return getEffectiveTrialEndTime(status);
  }
  return status.trialEndTime || status.formalEndTime || "";
}

// ── UI 推导（核心）──────────────────────────────────────

/**
 * 由 GET /status 推导 UI 视图模型 — 全模块唯一 UI 判断入口
 *
 * 页面请读 subscriptionState.view；需对任意 status 临时推导时可直调本函数。
 *
 * view 字段说明：
 * - mode         侧栏/布局主分支（VIEW_MODE）
 * - claim        顶部「立即领取」横幅（未开通且 canApplyTrial）
 * - trialBanner  顶部试用倒计时横幅（含到期 00）
 * - countdown    侧栏试用倒计时区域（mode=trial 时为 true）
 * - readonly     列表只读（仅删除）
 * - active       全功能可用
 * - list         评测页是否展示账户列表（全功能或只读均 true，未开通 false）
 * - vip          VIP 金边/角标（正式有效或试用已预购）
 * - grayLayout   灰色到期布局（trial-expired / expired）
 * - purchaseTip  购买弹窗提示「试用到期后生效」（serviceStatus=0）
 * - prepaid      试用期内已预购（pendingYears>0）
 *
 * @param {object|null|undefined} status GET /status 响应
 * @returns {ReturnType<emptyView>}
 */
export function buildView(status) {
  const view = emptyView();
  if (!status || typeof status !== "object") return view;

  // 从未开通：仅可能展示领取横幅
  if (!status.hasService) {
    view.claim = status.canApplyTrial === true;
    return view;
  }

  const ss = num(status.serviceStatus);
  const prep = prepaid(status);
  const readonly = isReadonly(status);
  const active = isActive(status);
  const banner = showTrialBanner(status);

  view.mode = resolveMode(status);
  view.readonly = readonly;
  view.trialBanner = banner;
  view.countdown = view.mode === VIEW_MODE.TRIAL;
  view.prepaid = prep;
  view.purchaseTip = ss === SERVICE_STATUS.TRIAL;
  view.active = active;
  view.list = active || readonly;
  view.vip = ss === SERVICE_STATUS.FORMAL || (ss === SERVICE_STATUS.TRIAL && prep);
  view.grayLayout =
    view.mode === VIEW_MODE.EXPIRED ||
    view.mode === VIEW_MODE.TRIAL_EXPIRED;

  return view;
}

/** @deprecated 请用 buildView */
export const resolveSubscriptionView = buildView;

/**
 * 由 GET /status 推导原 GET /active 响应结构（兼容旧接口消费方）
 *
 * @param {object|null|undefined} status
 * @returns {{
 *   active: boolean,
 *   serviceStatus: number|null,
 *   serviceStatusText: string,
 *   expireTime: string,
 *   daysLeft: number|null
 * }|null}
 */
export function buildActive(status) {
  if (!status || typeof status !== "object") return null;
  const ss = num(status.serviceStatus);
  const prep = Number(status.pendingYears) || 0;
  const expireRaw = getSubscriptionExpireTime(status);
  let daysLeft = status.daysLeft != null ? status.daysLeft : null;
  // 试用预购期间：剩余天数按叠加后的到期时间客户端重算
  if (ss === SERVICE_STATUS.TRIAL && prep > 0 && expireRaw) {
    daysLeft = computeDaysLeft(parseTrialEndTimestamp(expireRaw));
  }
  const view = buildView(status);
  return {
    active: view.active,
    serviceStatus: ss,
    serviceStatusText: status.serviceStatusText || "",
    expireTime: toDateOnly(expireRaw),
    daysLeft,
  };
}

/** @deprecated 请用 buildActive */
export const deriveActiveSnapshot = buildActive;

// ── 对外薄封装（兼容旧调用，内部均转调 buildView / 内部函数）──

/**
 * 试用是否已到期（同 trialEnded，供侧栏倒计时文案等使用）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
export function isTrialSubscriptionExpired(status) {
  return trialEnded(status);
}

/**
 * 订阅是否只读（同 buildView(status).readonly）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
export function isSubscriptionRestricted(status) {
  return buildView(status).readonly;
}

/**
 * 评测页是否应展示账户列表（同 buildView(status).list）
 * 未开通不展示；全功能或只读均展示
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
export function isSubscriptionListVisible(status) {
  return buildView(status).list;
}

/**
 * 是否曾有正式服务记录（IndivNav 区分正式到期角标等）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
export function hadFormalSubscription(status) {
  return hadFormal(status);
}

/**
 * 试用期内是否已预购（pendingYears > 0）
 * @param {object|null|undefined} status
 * @returns {boolean}
 */
export function hasPendingFormalPurchase(status) {
  return prepaid(status);
}

// ── API 请求 ────────────────────────────────────────────

/**
 * 本地测试开关：true 时 fetchSubscriptionStatus 直接返回 STATUS_OVERRIDE，不请求接口
 * 测完改回 false
 */
const USE_STATUS_OVERRIDE = false;

/**
 * 测试用 status，字段与 GET /status 一致，改这里即可切换页面 UI
 *
 * 常见场景示例（复制到对象里改）：
 * - 未开通：hasService=false, canApplyTrial=true
 * - 试用中：hasService=true, serviceStatus=0, trialStatus=1, pendingYears=0, trialEndTime 未来日期
 * - 试用到期：serviceStatus=0, trialStatus=2, pendingYears=0, trialEndTime 过去日期
 * - 试用预购：serviceStatus=0, pendingYears>0
 * - 正式有效：serviceStatus=1, formalEndTime
 * - 正式过期：serviceStatus=2, formalEndTime
 */
const STATUS_OVERRIDE = {
  hasService: true, // 是否有过服务记录（false=从未开通，可领试用）
  serviceStatus: 2, // 服务状态：0试用阶段 1正式有效 2已过期
  serviceStatusText: "试用中", // 服务状态文案（展示用）
  trialStatus: 2, // 试用状态：0未申请 1试用中 2试用已到期（仅 serviceStatus=0 时有效）
  pendingYears: 0, // 试用期内预购正式服务年数，>0 表示已预购
  trialStartTime: "", // 试用开始时间
  trialEndTime: "2026-7-18", // 试用结束时间（纯日期按当天 23:59:59 算到期）
  formalStartTime: "", // 正式服务开始时间
  formalEndTime: "2027-7-5", // 正式服务结束时间
  trialDays: 20, // 可领取试用天数（未开通时展示）
  daysLeft: 10, // 剩余天数（接口返回，预购时可能被客户端重算）
  canApplyTrial: false, // 无记录时是否可申请试用
  canPurchaseEarly: true, // 是否允许试用期内预购正式服务
};

/**
 * 请求 GET /status，获取订阅状态
 * 失败时 catch 返回 null，不抛错
 * @returns {Promise<object|null>}
 */
export function fetchSubscriptionStatus() {
  if (USE_STATUS_OVERRIDE) {
    return Promise.resolve({ ...STATUS_OVERRIDE });
  }
  return request.get(`${SUBSCRIPTION_API_BASE}/status`).catch(() => null);
}

/**
 * 签署协议 POST /agreement/sign
 * @param {number} [agreementType=AGREEMENT_TYPE_TRIAL] 0 试用 · 1 正式
 * @returns {Promise<object>}
 */
export function signAgreement(agreementType = AGREEMENT_TYPE_TRIAL) {
  return request.post(`${AGREEMENT_API_BASE}/sign`, null, {
    params: { agreementType },
  });
}

/**
 * 申请试用 POST /subscription/trial/apply
 * @param {number|string} agreementId 签署试用协议后返回的 agreementId
 * @returns {Promise<object>}
 */
export function applySubscriptionTrial(agreementId) {
  return request.post(`${SUBSCRIPTION_API_BASE}/trial/apply`, null, {
    params: { agreementId },
  });
}

// ── 状态写入与刷新 ──────────────────────────────────────

/**
 * 将 GET /status 响应写入 subscriptionState，并推导 view / activeData
 * 由 executeStatusRefresh 调用，外部一般不直接使用
 *
 * @param {object|null} status
 */
function applySubscriptionStatus(status) {
  subscriptionState.status = status;
  subscriptionState.trialDays =
    status && status.trialDays != null ? Number(status.trialDays) : 14;
  subscriptionState.view = buildView(status);
  subscriptionState.activeData = buildActive(status);
  subscriptionState.loaded = true;
}

/**
 * 拉取失败且非 silent 时清空派生数据（保留 loading / loaded 等会话字段）
 */
function clearSubscriptionDerivedState() {
  subscriptionState.status = null;
  subscriptionState.activeData = null;
  subscriptionState.view = emptyView();
}

/**
 * 执行一次 /status 拉取并写入状态
 * @param {boolean} silent 为 true 时失败不清空已有数据
 * @returns {Promise<object|null>}
 */
async function executeStatusRefresh(silent) {
  try {
    const status = await fetchSubscriptionStatus();
    applySubscriptionStatus(status);
    return status;
  } catch (e) {
    if (!silent) clearSubscriptionDerivedState();
    return subscriptionState.status;
  } finally {
    subscriptionState.loading = false;
    inflightStatusRequest = null;
  }
}

/**
 * 刷新订阅状态 — indivSession 统一入口
 *
 * @param {object} [options]
 * @param {boolean} [options.silent=false] 不置 loading；失败时是否保留旧数据
 * @param {boolean} [options.force=false] 已 loaded 时仍强制重新请求
 * @returns {Promise<object|null>} 最新 status
 */
export function refreshSubscriptionStatus(options = {}) {
  const { silent = subscriptionState.loaded, force = false } = options;
  if (!force && subscriptionState.loaded) {
    return Promise.resolve(subscriptionState.status);
  }
  if (inflightStatusRequest) return inflightStatusRequest;
  if (!silent) subscriptionState.loading = true;
  inflightStatusRequest = executeStatusRefresh(silent);
  return inflightStatusRequest;
}

/**
 * 退出登录时重置订阅状态（清空数据与进行中的请求）
 */
export function resetSubscriptionState() {
  subscriptionState.loading = false;
  subscriptionState.loaded = false;
  subscriptionState.trialDays = 14;
  subscriptionState.status = null;
  subscriptionState.activeData = null;
  subscriptionState.view = emptyView();
  inflightStatusRequest = null;
}



// WEBPACK FOOTER //
// ./src/view/user/subscriptionService/subscriptionService.js